import { attachmentRefusal, formatFileSize } from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import type { EmailAttachment } from "../../lib/gmail";

/**
 * Files on an email, picked from the phone or the computer.
 *
 * Jonathon, 2026-09-10: "when emailing inside of roofboard there needs to be
 * the option of attaching items." On a phone the one button offers the camera,
 * the photo library and Files, which is where a rep keeps everything they would
 * want to send.
 *
 * **Photos are shrunk before they count.** A phone photo is 3–5 MB and a
 * customer needs to see the rotten decking, not print it on a billboard. Anything
 * over 1.5 MB that the browser can draw goes out as a JPEG no longer than 2000
 * pixels on its longest side — a few hundred KB, and still sharp on a laptop.
 * One the browser cannot draw goes as it is.
 */

export interface PickedFile extends EmailAttachment {
  key: string;
  /** The real size, before base64 made it a third larger. */
  bytes: number;
}

const SHRINK_OVER_BYTES = 1_500_000;
const LONGEST_SIDE = 2000;
const DRAWABLE = /^image\/(jpeg|png|webp|heic|heif)$/i;

/** A photo big enough to be redrawn smaller; every other file goes as it is. */
const willShrink = (file: File): boolean =>
  DRAWABLE.test(file.type) && file.size > SHRINK_OVER_BYTES;

async function shrinkPhoto(file: File): Promise<{ blob: Blob; name: string }> {
  const original = { blob: file as Blob, name: file.name };
  if (!willShrink(file)) return original;
  let bitmap: ImageBitmap | null = null;
  try {
    // Said outright: browsers have disagreed on the default, and the JPEG
    // drawn here drops the EXIF that would have stood it the right way up.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale = Math.min(
      1,
      LONGEST_SIDE / Math.max(bitmap.width, bitmap.height),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    if (!context) return original;
    // JPEG has no transparency; without this a screenshot's clear corners
    // arrive black.
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.82),
    );
    if (!blob || blob.size >= file.size) return original;
    return { blob, name: file.name.replace(/\.[^.]+$/, "") + ".jpg" };
  } catch {
    // Not every browser draws every format — HEIC outside Safari, mostly.
    return original;
  } finally {
    // A phone photo decoded is a couple of hundred MB. Held on to, a handful
    // of them is how a phone's browser reloads the tab and loses the draft.
    bitmap?.close();
  }
}

function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(String(reader.result ?? "").replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

let counter = 0;

async function readForEmail(file: File): Promise<PickedFile> {
  const { blob, name } = await shrinkPhoto(file);
  counter += 1;
  return {
    key: `${counter}-${name}`,
    filename: name,
    mimeType: blob.type || file.type || "application/octet-stream",
    content: await base64Of(blob),
    bytes: blob.size,
  };
}

/** What the Edge Function is sent — no keys, no sizes. */
export function toAttachments(files: readonly PickedFile[]): EmailAttachment[] {
  return files.map(({ filename, mimeType, content }) => ({
    filename,
    mimeType,
    content,
  }));
}

export function AttachmentPicker({
  files,
  onChange,
  disabled,
}: {
  files: readonly PickedFile[];
  onChange: (files: PickedFile[]) => void;
  disabled?: boolean;
}) {
  const [reading, setReading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function add(chosen: File[]) {
    if (chosen.length === 0) return;
    setProblem(null);
    // Refused before anything is read wherever the answer is already known: a
    // file that will not be shrunk is the size it is, and an eleventh file is
    // an eleventh file. Reading a 300 MB video into memory only to say it is
    // too big is how a phone loses the tab. A photo that will be shrunk counts
    // as nothing here, and is weighed properly once it has been.
    const early = attachmentRefusal([
      ...files.map((file) => file.bytes),
      ...chosen.map((file) => (willShrink(file) ? 0 : file.size)),
    ]);
    if (early) {
      setProblem(t(early));
      return;
    }
    setReading(true);
    try {
      // One at a time, so no more than one photo is ever decoded at once.
      const read: PickedFile[] = [];
      for (const file of chosen) read.push(await readForEmail(file));
      const next = [...files, ...read];
      const refusal = attachmentRefusal(next.map((file) => file.bytes));
      if (refusal) {
        setProblem(t(refusal));
        return;
      }
      onChange(next);
    } catch {
      setProblem(t("That file could not be read. Try choosing it again."));
    } finally {
      setReading(false);
    }
  }

  return (
    <div className="field full">
      <label
        className="btn-ghost"
        style={{ display: "inline-block", cursor: "pointer", width: "auto" }}
      >
        {reading ? t("Reading…") : `📎 ${t("Attach files")}`}
        <input
          type="file"
          multiple
          hidden
          disabled={disabled || reading}
          onChange={(event) => {
            // Copied before the reset: the FileList is live, and emptying the
            // input is what lets the same file be chosen twice.
            const chosen = Array.from(event.target.files ?? []);
            event.target.value = "";
            void add(chosen);
          }}
        />
      </label>

      {files.map((file) => (
        <div
          key={file.key}
          className="na-hint"
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <span>
            {file.filename} · {formatFileSize(file.bytes)}
          </span>
          <button
            type="button"
            className="btn-ghost"
            aria-label={t("Remove {name}", { name: file.filename })}
            disabled={disabled}
            onClick={() => onChange(files.filter((f) => f.key !== file.key))}
          >
            ✕
          </button>
        </div>
      ))}

      {problem && (
        <div className="err" role="alert" style={{ marginTop: 4 }}>
          {problem}
        </div>
      )}
    </div>
  );
}
